import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import {deleteUser, getUserById, getUserRoles, getUsers, updateUser} from "../api/api";
import {User, UserProfileResponse, UserRole} from "../types";
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Modal,
    Pagination,
    Paper,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import {useAlert} from "../context/AlertContext";
import AppModalBox from "./AppModalBox";
import AppTableHeaderCell from "./AppTableHeaderCell";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import BarChartIcon from "@mui/icons-material/BarChart";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";

interface Props {
    onAdd: () => void;
    refreshTrigger: boolean;
    refreshUsers: () => void;
}

const getInitials = (fullName?: string) => {
    if (!fullName || !fullName.trim()) return "?";

    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const UsersList = ({onAdd, refreshTrigger, refreshUsers}: Props) => {
    const {t} = useTranslation();
    const {showAlert} = useAlert();

    const [users, setUsers] = useState<User[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchEmail, setSearchEmail] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const pageSize = 10;

    const [loading, setLoading] = useState(false);

    const [userToDelete, setUserToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editModalLoading, setEditModalLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);

    const [roles, setRoles] = useState<UserRole[]>([]);
    const [rolesLoading, setRolesLoading] = useState(false);

    const [editFullName, setEditFullName] = useState("");
    const [editEmail, setEditEmail] = useState("");
    const [editPassword, setEditPassword] = useState("");
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);

    const fetchUsersPage = async () => {
        try {
            setLoading(true);
            const {users, totalPages} = await getUsers(page - 1, pageSize, searchEmail);
            setUsers(users);
            setTotalPages(totalPages);
        } catch (error: any) {
            showAlert(error?.message ?? t("users.errors.loadFailed"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsersPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, refreshTrigger, searchEmail]);

    useEffect(() => {
        setPage(1);
    }, [searchEmail]);

    const openEdit = async (user: User) => {
        try {
            setEditingUser(user);
            setEditModalLoading(true);

            let allRoles = roles;
            if (!allRoles.length) {
                setRolesLoading(true);
                allRoles = await getUserRoles();
                setRoles(allRoles);
            }

            const details: UserProfileResponse = await getUserById(user.id);

            setEditFullName(details.fullName ?? "");
            setEditEmail(details.email ?? "");
            setEditPassword("");

            const byId = new Set(
                (details.roles as any[])
                    .map((r) => (r && typeof r === "object" ? (r as any).id : null))
                    .filter((id): id is number => typeof id === "number")
            );

            const byName = new Set((details.roles ?? []).map((r) => r.name));

            const preselected = allRoles.filter((role) => byId.has(role.id) || byName.has(role.name));
            setSelectedRoles(preselected);

            if (preselected.length === 0) {
                showAlert(t("users.alerts.noRolesSelected"), "warning");
            }
        } catch (error: any) {
            showAlert(error?.message || t("users.errors.loadDetailsFailed"), "error");
            setEditingUser(null);
        } finally {
            setEditModalLoading(false);
            setRolesLoading(false);
        }
    };

    const closeEditModal = () => {
        setEditingUser(null);
        setEditModalLoading(false);
        setEditSaving(false);
        setEditPassword("");
        setSelectedRoles([]);
    };

    const handleEditUser = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!editingUser) return;

        if (selectedRoles.length === 0) {
            showAlert(t("users.errors.atLeastOneRoleRequired"), "error");
            return;
        }

        try {
            setEditSaving(true);

            await updateUser({
                id: editingUser.id,
                fullName: editFullName.trim(),
                email: editEmail.trim(),
                ...(editPassword.trim() ? {password: editPassword.trim()} : {}),
                roles: selectedRoles.map((role) => ({id: role.id})),
            });

            showAlert(t("users.alerts.updated"), "success");
            closeEditModal();
            refreshUsers?.();
        } catch (error: any) {
            showAlert(error?.message || t("users.errors.updateFailed"), "error");
        } finally {
            setEditSaving(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            setDeleteLoading(true);
            await deleteUser(Number(userToDelete));

            showAlert(t("users.alerts.deleted"), "success");
            setUserToDelete(null);
            refreshUsers?.();
        } catch (error: any) {
            showAlert(error?.message || t("users.errors.deleteFailed"), "error");
        } finally {
            setDeleteLoading(false);
        }
    };

    const onSearch = () => {
        setSearchEmail(searchInput.trim());
    };

    return (
        <>
            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: {xs: "stretch", sm: "center"},
                            gap: 1.5,
                            px: {xs: 1.5, md: 3},
                            py: 2,
                            flexDirection: {xs: "column", sm: "row"},
                            backgroundColor: theme.palette.background.paper,
                            borderBottom: `1px solid ${theme.palette.action.hover}`,
                        })}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: {xs: "100%", sm: "auto"},
                            }}
                        >
                            <TextField
                                type="search"
                                size="small"
                                placeholder={t("users.searchByEmail")}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        onSearch();
                                    }
                                }}
                                sx={{
                                    width: {xs: "100%", sm: 320},
                                    flexGrow: {xs: 1, sm: 0},
                                }}
                            />

                            <IconButton
                                onClick={onSearch}
                                disabled={loading}
                                aria-label={t("common.search")}
                                sx={(theme) => ({
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: theme.palette.action.hover,
                                    color: theme.palette.text.primary,
                                    flexShrink: 0,
                                    "&:hover": {
                                        backgroundColor: theme.palette.action.selected,
                                    },
                                })}
                            >
                                <SearchIcon fontSize="small"/>
                            </IconButton>
                        </Box>

                        <Box sx={{flexGrow: 1, display: {xs: "none", sm: "block"}}}/>

                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                width: {xs: "100%", sm: "auto"},
                                px: {xs: 1.25, sm: 0},
                                py: {xs: 1, sm: 0},
                                borderRadius: {xs: 2.5, sm: 0},
                                backgroundColor: {
                                    xs: theme.palette.action.hover,
                                    sm: "transparent",
                                },
                            })}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{fontWeight: 700}}>
                                {t("common.pageOf", {page, totalPages})}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{fontWeight: 700, display: {xs: "block", sm: "none"}}}
                            >
                                {t("users.shown", {count: users.length})}
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={onAdd}
                            aria-label={t("users.addUser")}
                            sx={(theme) => ({
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                display: {xs: "none", sm: "inline-flex"},
                                "&:hover": {
                                    backgroundColor: theme.palette.action.selected,
                                },
                            })}
                        >
                            <AddIcon fontSize="small"/>
                        </IconButton>

                        <Button
                            onClick={onAdd}
                            variant="contained"
                            startIcon={<PersonAddAlt1Icon fontSize="small"/>}
                            sx={{
                                display: {xs: "inline-flex", sm: "none"},
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 800,
                                py: 0.95,
                            }}
                        >
                            {t("users.addUser")}
                        </Button>
                    </Box>

                    {users.length === 0 ? (
                        <Box sx={{py: 10, px: 3, textAlign: "center"}}>
                            <Typography variant="h6" sx={{mb: 1}}>
                                {t("users.noUsersFound")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("users.noUsersDescription")}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{display: {xs: "none", md: "block"}, width: "100%", overflowX: "auto"}}>
                                <table style={{width: "100%", borderCollapse: "separate", borderSpacing: 0}}>
                                    <thead>
                                    <tr style={{background: "#fafafa"}}>
                                        <AppTableHeaderCell>{t("users.user")}</AppTableHeaderCell>
                                        <AppTableHeaderCell>{t("users.email")}</AppTableHeaderCell>
                                        <AppTableHeaderCell align="right">{t("common.actions")}</AppTableHeaderCell>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {users.map((user) => (
                                        <tr
                                            key={user.id}
                                            style={{
                                                transition: "background-color 0.2s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#fafafa";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "transparent";
                                            }}
                                        >
                                            <td style={{padding: "18px 16px"}}>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                    <Avatar
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            bgcolor: "grey.300",
                                                            color: "text.primary",
                                                        }}
                                                    >
                                                        {getInitials(user.fullName)}
                                                    </Avatar>

                                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                                        {user.fullName || "—"}
                                                    </Typography>
                                                </Box>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Typography variant="body2">
                                                    {user.email}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px", textAlign: "right"}}>
                                                <Box sx={{display: "inline-flex", gap: 1}}>
                                                    <Tooltip title={t("users.analytics")}>
                                                        <IconButton
                                                            component={RouterLink}
                                                            to={`/admin/users/${user.id}/analytics`}
                                                            size="small"
                                                            aria-label={t("users.analytics")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <BarChartIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title={t("common.edit")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEdit(user)}
                                                            aria-label={t("common.edit")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <EditIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title={t("common.delete")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setUserToDelete(user.id.toString())}
                                                            aria-label={t("common.delete")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <DeleteIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </Box>

                            <Box
                                sx={{
                                    display: {xs: "flex", md: "none"},
                                    flexDirection: "column",
                                    gap: 1.25,
                                    p: 1.5,
                                }}
                            >
                                <Box
                                    sx={(theme) => ({
                                        p: 1.5,
                                        borderRadius: 3,
                                        background:
                                            theme.palette.mode === "dark"
                                                ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
                                                : "linear-gradient(135deg, rgba(248,250,252,1), rgba(241,245,249,1))",
                                        border: `1px solid ${theme.palette.divider}`,
                                    })}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                                        <Box sx={{minWidth: 0}}>
                                            <Typography sx={{fontWeight: 900, lineHeight: 1.2}}>
                                                {t("users.adminUsers")}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary">
                                                {t("users.adminUsersDescription")}
                                            </Typography>
                                        </Box>

                                        <Chip
                                            size="small"
                                            label={t("users.shown", {count: users.length})}
                                            sx={(theme) => ({
                                                fontWeight: 800,
                                                backgroundColor: theme.palette.action.hover,
                                                flexShrink: 0,
                                            })}
                                        />
                                    </Box>
                                </Box>

                                {users.map((user) => (
                                    <Box
                                        key={user.id}
                                        sx={(theme) => ({
                                            p: 1.5,
                                            borderRadius: 3,
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: theme.palette.background.paper,
                                            boxShadow:
                                                theme.palette.mode === "dark"
                                                    ? "0 14px 32px rgba(0,0,0,0.32)"
                                                    : "0 14px 32px rgba(15,23,42,0.07)",
                                            overflow: "hidden",
                                            position: "relative",
                                            "&:before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                bottom: 0,
                                                left: 0,
                                                width: 4,
                                                backgroundColor: theme.palette.primary.main,
                                            },
                                        })}
                                    >
                                        <Box sx={{display: "flex", alignItems: "flex-start", gap: 1.25}}>
                                            <Avatar
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    fontSize: 14,
                                                    fontWeight: 900,
                                                    bgcolor: "grey.300",
                                                    color: "text.primary",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {getInitials(user.fullName)}
                                            </Avatar>

                                            <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 900,
                                                        lineHeight: 1.35,
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {user.fullName || "—"}
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        mt: 0.75,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.75,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <EmailOutlinedIcon
                                                        sx={{
                                                            fontSize: 17,
                                                            color: "text.secondary",
                                                            flexShrink: 0,
                                                        }}
                                                    />

                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            wordBreak: "break-word",
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        {user.email || "—"}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={(theme) => ({
                                                        mt: 1,
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        px: 1,
                                                        py: 0.45,
                                                        borderRadius: 999,
                                                        backgroundColor: theme.palette.action.hover,
                                                    })}
                                                >
                                                    <ManageAccountsOutlinedIcon
                                                        sx={{
                                                            fontSize: 15,
                                                            color: "text.secondary",
                                                        }}
                                                    />

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{fontWeight: 800}}
                                                    >
                                                        {t("users.userId")}: {user.id}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                display: "grid",
                                                gridTemplateColumns: "1fr 1fr",
                                                gap: 1,
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                component={RouterLink}
                                                to={`/admin/users/${user.id}/analytics`}
                                                startIcon={<BarChartIcon fontSize="small"/>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("users.analytics")}
                                            </Button>

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => openEdit(user)}
                                                startIcon={<EditIcon fontSize="small"/>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("common.edit")}
                                            </Button>
                                        </Box>

                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => setUserToDelete(user.id.toString())}
                                            startIcon={<DeleteIcon fontSize="small"/>}
                                            sx={{
                                                mt: 1,
                                                borderRadius: 2,
                                                textTransform: "none",
                                                fontWeight: 800,
                                                py: 0.85,
                                            }}
                                        >
                                            {t("users.deleteUser")}
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    disabled={loading}
                />
            </Box>

            <DeleteConfirmationModal
                open={userToDelete !== null}
                onClose={() => (deleteLoading ? null : setUserToDelete(null))}
                onConfirm={handleDeleteUser}
            />

            <Modal open={editingUser !== null} onClose={closeEditModal}>
                <AppModalBox
                    sx={{
                        p: {xs: 2, sm: 3, md: 4},
                        margin: "auto",
                        width: {xs: "calc(100vw - 24px)", sm: 520},
                        maxWidth: "95vw",
                        maxHeight: "95vh",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                        <Avatar
                            sx={(theme) => ({
                                width: 42,
                                height: 42,
                                fontSize: 13,
                                fontWeight: 900,
                                bgcolor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                flexShrink: 0,
                            })}
                        >
                            {editingUser ? getInitials(editingUser.fullName) : "?"}
                        </Avatar>

                        <Box sx={{minWidth: 0}}>
                            <Typography variant="h6" sx={{fontWeight: 900, lineHeight: 1.2}} noWrap>
                                {t("users.editUser")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" noWrap>
                                {editingUser?.email ?? t("users.updateUserDetails")}
                            </Typography>
                        </Box>
                    </Box>

                    {editModalLoading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 4}}>
                            <CircularProgress/>
                        </Box>
                    ) : (
                        <form onSubmit={handleEditUser}>
                            <TextField
                                label={t("users.fullName")}
                                fullWidth
                                required
                                value={editFullName}
                                onChange={(e) => setEditFullName(e.target.value)}
                                disabled={editSaving}
                                sx={{mb: 2}}
                            />

                            <TextField
                                label={t("users.email")}
                                type="email"
                                fullWidth
                                required
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                disabled={editSaving}
                                sx={{mb: 2}}
                            />

                            <TextField
                                label={t("users.newPasswordOptional")}
                                type="password"
                                fullWidth
                                value={editPassword}
                                onChange={(e) => setEditPassword(e.target.value)}
                                disabled={editSaving}
                                helperText={t("users.keepCurrentPassword")}
                                sx={{mb: 2}}
                            />

                            <Autocomplete
                                multiple
                                options={roles}
                                loading={rolesLoading}
                                value={selectedRoles}
                                onChange={(_, value) => setSelectedRoles(value)}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("users.roles")}
                                        placeholder={rolesLoading ? t("common.loading") : t("users.selectRoles")}
                                        disabled={editSaving || rolesLoading}
                                        sx={{mb: 2}}
                                    />
                                )}
                            />

                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: "flex-end",
                                    flexDirection: {xs: "column-reverse", sm: "row"},
                                }}
                            >
                                <Button
                                    onClick={closeEditModal}
                                    disabled={editSaving}
                                    sx={{
                                        textTransform: "none",
                                        width: {xs: "100%", sm: "auto"},
                                    }}
                                >
                                    {t("common.cancel")}
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={editSaving || rolesLoading || selectedRoles.length === 0}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 800,
                                        width: {xs: "100%", sm: "auto"},
                                    }}
                                >
                                    {editSaving ? t("users.saving") : t("users.saveChanges")}
                                </Button>
                            </Box>

                            <Typography
                                variant="caption"
                                sx={{display: "block", mt: 2, color: "text.secondary"}}
                            >
                                {t("users.passwordUpdateNote")}
                            </Typography>
                        </form>
                    )}
                </AppModalBox>
            </Modal>
        </>
    );
};

export default UsersList;